import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Divider,
    ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CartNotificationItem from "./cartNotificationItem";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useHistory } from "react-router-dom";
import { CART_NOTIFICATION } from "../../../../locales/components/namespaces";
import useCartData from "../../../../hooks/cart/useCartData";
import useCartAction from "../../../../hooks/cart/useCartAction";

const CartNotification = ({ open, onClose, onOpen }) => {
    const history = useHistory();
    const { t } = useTranslation(CART_NOTIFICATION);

    const { lastAddedItem,shouldShowLastAddedNotification  } = useCartData();
    const { clearLastAdded } = useCartAction();

    useEffect(() => {
        if (lastAddedItem && shouldShowLastAddedNotification && !open) {
            onOpen?.();
        }
    }, [lastAddedItem, shouldShowLastAddedNotification, open, onOpen]);

    const handleClickAway = () => {
        if (open) {
            clearLastAdded();
            onClose();
        }
    };

    const handleClose = () => {
        clearLastAdded();
        onClose();
    };

    const handleCheckout = () => {
        history.push("/cart");
        clearLastAdded();
        onClose();
    };

    const handleContinueShopping = () => {
        clearLastAdded();
        onClose();
    };

    if (!lastAddedItem || !shouldShowLastAddedNotification) {
        return null;
    }

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                sx={{
                    position: "fixed",
                    right: 0,
                    top: 0,
                    width: { xs: "100%", sm: "26.1875rem" },
                    height: "100vh",
                    borderRadius: "30px 0 0 30px",
                    backgroundColor: "#FCFCFC",
                    boxShadow: "-2px 0 12px rgba(0, 0, 0, 0.15)",
                    transform: open ? "translateX(0)" : "translateX(100%)",
                    transition: "transform 0.3s ease",
                    zIndex: 1300,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <IconButton
                        onClick={handleClose}
                        size="large"
                        sx={{
                            color: "#111",
                            bgcolor: "#F0F0F0",
                            borderRadius: "50px",
                            "&:hover": { bgcolor: "#fff" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: "20px",
                        fontFamily: "Roboto",
                        color: "#111",
                    }}
                >
                    {t("product_added_success")}
                </Typography>

                <Box sx={{ pt: 2 }}>
                    <CartNotificationItem product={lastAddedItem} isMainProduct />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                    sx={{
                        mt: "auto",
                        p: 2,
                        backgroundColor: "#fff",
                        borderTop: "1px solid #e0e0e0",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCheckout}
                        sx={{
                            color: "#fff",
                            fontWeight: 900,
                            fontSize: "14px",
                            fontFamily: "Zen Maru Gothic",
                            py: 1.5,
                        }}
                    >
                        {t("view_cart")}
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleContinueShopping}
                        sx={{
                            fontWeight: 900,
                            fontSize: "14px",
                            fontFamily: "Zen Maru Gothic",
                            lineHeight: "120%",
                            py: 1.5,
                            borderRadius: "24px",
                        }}
                    >
                        <KeyboardArrowLeftIcon sx={{ pt: 0.4 }} />
                        {t("continue_shopping")}
                    </Button>
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default CartNotification;